<template>
    <UserLayout :page-title="t('finance')" :page-section="t('fundManagement')">
        <div class="fin-wrap">
            <div class="fin-inner">

                <!-- ── HEADER STRIP ── -->
                <div class="fin-header anim-up" style="--d:0ms">
                    <div class="fin-header-l">
                        <div class="fin-header-icon">
                            <svg width="17" height="17" fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                            </svg>
                        </div>
                        <div>
                            <p class="fin-header-sup">{{ t('finance') }}</p>
                            <h1 class="fin-header-title">{{ t('fundManagement') }}</h1>
                        </div>
                    </div>
                    <div class="fin-bal-chips">
                        <div class="fin-bal-chip">
                            <span class="fin-chip-lbl">USDT Balance</span>
                            <span class="fin-chip-val" :style="`color:${themeColors.primary}`">${{ parseFloat(walletBalance).toFixed(2) }}</span>
                        </div>
                        <div class="fin-bal-chip fin-bal-chip--blue">
                            <span class="fin-chip-lbl">DS-Credits</span>
                            <span class="fin-chip-val" style="color:#60a5fa">${{ parseFloat(creditBalance).toFixed(2) }}</span>
                        </div>
                        <div class="fin-bal-chip" style="background:rgba(16,185,129,0.08);border-color:rgba(16,185,129,0.22)">
                            <span class="fin-chip-lbl">E-wallet</span>
                            <span class="fin-chip-val" style="color:#10b981">${{ parseFloat(ewalletBalance).toFixed(2) }}</span>
                        </div>
                        <div class="fin-bal-chip" style="background:rgba(255,111,0,0.08);border-color:rgba(255,111,0,0.22)">
                            <span class="fin-chip-lbl">Rio Wallet</span>
                            <span class="fin-chip-val" style="color:#FF6F00">₿{{ formatBtcAmount(rioBalance) }}</span>
                        </div>
                    </div>
                </div>

                <!-- ── STAT CARDS ── -->
                <div class="fin-stat-row anim-up" style="--d:50ms">
                    <div class="fin-stat" style="--ac:167,93,237">
                        <div class="fin-stat-glow" style="background:radial-gradient(ellipse at 100% 100%,rgba(167,93,237,0.22),transparent 70%)"></div>
                        <div class="fin-stat-top">
                            <div class="fin-stat-ic" style="background:rgba(167,93,237,0.15);box-shadow:0 0 12px rgba(167,93,237,0.2)">
                                <svg width="14" height="14" fill="none" stroke="#a75ded" stroke-width="2.2" viewBox="0 0 24 24"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                            </div>
                            <span class="fin-stat-lbl">USDT Balance</span>
                        </div>
                        <p class="fin-stat-val" :style="`color:${themeColors.primary}`">${{ parseFloat(walletBalance).toFixed(2) }}</p>
                        <div class="fin-stat-sub" :style="`color:${themeColors.primary}`">Available to withdraw</div>
                        <div class="fin-bar-track"><div class="fin-bar fin-bar--shimmer" style="width:100%;background:linear-gradient(90deg,#7c3aed,#a75ded,#c084fc)"></div></div>
                    </div>
                    <div class="fin-stat" style="--ac:96,165,250">
                        <div class="fin-stat-glow" style="background:radial-gradient(ellipse at 100% 100%,rgba(96,165,250,0.18),transparent 70%)"></div>
                        <div class="fin-stat-top">
                            <div class="fin-stat-ic" style="background:rgba(96,165,250,0.12);box-shadow:0 0 12px rgba(96,165,250,0.18)">
                                <svg width="14" height="14" fill="none" stroke="#60a5fa" stroke-width="2.2" viewBox="0 0 24 24"><path stroke-linecap="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                            </div>
                            <span class="fin-stat-lbl">DS-Credits</span>
                        </div>
                        <p class="fin-stat-val" style="color:#60a5fa">${{ parseFloat(creditBalance).toFixed(2) }}</p>
                        <div class="fin-stat-sub" style="color:#60a5fa">Non-withdrawable</div>
                        <div class="fin-bar-track"><div class="fin-bar fin-bar--shimmer" style="width:75%;background:linear-gradient(90deg,#1d4ed8,#60a5fa,#93c5fd)"></div></div>
                    </div>
                    <div class="fin-stat" style="--ac:16,185,129">
                        <div class="fin-stat-glow" style="background:radial-gradient(ellipse at 100% 100%,rgba(16,185,129,0.18),transparent 70%)"></div>
                        <div class="fin-stat-top">
                            <div class="fin-stat-ic" style="background:rgba(16,185,129,0.12);box-shadow:0 0 12px rgba(16,185,129,0.18)">
                                <svg width="14" height="14" fill="none" stroke="#10b981" stroke-width="2.2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                            </div>
                            <span class="fin-stat-lbl">E-wallet</span>
                        </div>
                        <p class="fin-stat-val" style="color:#10b981">${{ parseFloat(ewalletBalance).toFixed(2) }}</p>
                        <div class="fin-stat-sub" style="color:#10b981">Available Wallets</div>
                        <div class="fin-bar-track"><div class="fin-bar fin-bar--shimmer" style="width:55%;background:linear-gradient(90deg,#059669,#10b981,#34d399)"></div></div>
                    </div>
                    <div class="fin-stat" style="--ac:16,185,129">
                        <div class="fin-stat-glow" style="background:radial-gradient(ellipse at 100% 100%,rgba(16,185,129,0.18),transparent 70%)"></div>
                        <div class="fin-stat-top">
                            <div class="fin-stat-ic" style="background:rgba(16,185,129,0.12);box-shadow:0 0 12px rgba(16,185,129,0.18)">
                                <svg width="14" height="14" fill="none" stroke="#10b981" stroke-width="2.2" viewBox="0 0 24 24"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
                            </div>
                            <span class="fin-stat-lbl">Total Deposits</span>
                        </div>
                        <p class="fin-stat-val" style="color:#10b981">{{ deposits?.total || 0 }}</p>
                        <div class="fin-stat-sub" style="color:#10b981">All time</div>
                        <div class="fin-bar-track"><div class="fin-bar fin-bar--shimmer" style="width:60%;background:linear-gradient(90deg,#059669,#10b981,#34d399)"></div></div>
                    </div>
                    <div class="fin-stat" style="--ac:251,191,36">
                        <div class="fin-stat-glow" style="background:radial-gradient(ellipse at 100% 100%,rgba(251,191,36,0.15),transparent 70%)"></div>
                        <div class="fin-stat-top">
                            <div class="fin-stat-ic" style="background:rgba(251,191,36,0.1);box-shadow:0 0 12px rgba(251,191,36,0.15)">
                                <svg width="14" height="14" fill="none" stroke="#fbbf24" stroke-width="2.2" viewBox="0 0 24 24"><path stroke-linecap="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
                            </div>
                            <span class="fin-stat-lbl">Total Withdrawals</span>
                        </div>
                        <p class="fin-stat-val" style="color:#fbbf24">{{ withdrawals?.total || 0 }}</p>
                        <div class="fin-stat-sub" style="color:#fbbf24">All time</div>
                        <div class="fin-bar-track"><div class="fin-bar fin-bar--shimmer" style="width:45%;background:linear-gradient(90deg,#b45309,#fbbf24,#fde68a)"></div></div>
                    </div>
                    <div class="fin-stat fin-stat--rio" style="--ac:255,111,0">
                        <div class="fin-stat-glow" style="background:radial-gradient(ellipse at 100% 100%,rgba(255,111,0,0.18),transparent 70%)"></div>
                        <div class="fin-stat-top">
                            <div class="fin-stat-ic" style="background:rgba(255,111,0,0.12);box-shadow:0 0 12px rgba(255,111,0,0.18)">
                                <svg width="14" height="14" fill="none" stroke="#FF6F00" stroke-width="2.2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 6v6l4 2"/></svg>
                            </div>
                            <span class="fin-stat-lbl">BTC Rewards ₿</span>
                        </div>
                        <p class="fin-stat-val" style="color:#FF6F00">₿{{ formatBtcAmount(rioBalance) }}</p>
                        <div class="fin-stat-sub" style="color:#FF6F00">Investment profits</div>
                        <div class="fin-bar-track"><div class="fin-bar fin-bar--shimmer" style="width:40%;background:linear-gradient(90deg,#ff6f00,#ffb74d,#ffe0b2)"></div></div>
                    </div>
                </div>

                <!-- ── TAB BAR ── -->
                <div class="fin-tabs-wrap anim-up" style="--d:100ms">
                    <div class="fin-tabs" role="tablist">
                        <button
                            v-for="tab in tabs"
                            :key="tab.value"
                            class="fin-tab"
                            :class="activeTab === tab.value ? 'fin-tab--on' : ''"
                            :style="activeTab === tab.value
                                ? `background:linear-gradient(135deg,#7c3aed,${themeColors.primary});box-shadow:0 4px 18px rgba(${themeColors.primaryRgb},0.38);`
                                : ''"
                            @click="activeTab = tab.value"
                            role="tab"
                            :aria-selected="activeTab === tab.value"
                        >
                            <svg class="fin-tab-ic" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" v-html="tab.icon"></svg>
                            {{ tab.label }}
                        </button>
                    </div>
                </div>

                <!-- ── TAB PANELS ── -->
                <div class="fin-panels anim-up" style="--d:150ms">
                    <DepositComponent
                        v-if="activeTab === 'deposit'"
                        :deposits="deposits"
                        :payment-gateways="paymentGateways"
                        :wallet-balance="walletBalance"
                        :bsc-wallets="bscWallets"
                    />

                    <WithdrawComponent
                        v-else-if="activeTab === 'withdraw'"
                        :withdrawals="withdrawals"
                        :withdrawal-gateways="withdrawalGateways"
                        :wallet-balance="walletBalance"
                        :ewallet-balance="ewalletBalance"
                        :bsc-wallets="bscWallets"
                    />

                    <!-- ── RIO CONVERT PANEL ── -->
                    <RioConvertComponent
                        v-else-if="activeTab === 'rio'"
                        :rio-balance="rioBalance"
                        :wallet-balance="walletBalance"
                        :ewallet-balance="ewalletBalance"
                        :rio-conversions="rioConversions"
                    />

                    <!-- ── TRANSFER PANEL ── -->
                    <div v-else-if="activeTab === 'transfer'" class="fin-xfer">
                        <div class="fin-xfer-grid">

                            <!-- Main form card -->
                            <div class="fin-card">
                                <div class="fin-card-topline" :style="`background:linear-gradient(90deg,transparent,rgba(${themeColors.primaryRgb},0.5),rgba(232,121,249,0.25),transparent)`"></div>

                                <div class="fin-xfer-head">
                                    <div class="fin-xfer-icon" :style="`background:rgba(${themeColors.primaryRgb},0.12);border-color:rgba(${themeColors.primaryRgb},0.25);color:${themeColors.primary};`">
                                        <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 class="fin-xfer-title">Transfer DS-Credits</h2>
                                        <p class="fin-xfer-sub">Instantly send credits to any member</p>
                                    </div>
                                </div>

                                <!-- Balance pill -->
                                <div class="fin-xfer-balance" :style="`border-color:rgba(${themeColors.primaryRgb},0.18);background:rgba(${themeColors.primaryRgb},0.05);`">
                                    <div>
                                        <p class="fin-xfer-bal-lbl">Available Credits</p>
                                        <p class="fin-xfer-bal-val" :style="`color:${themeColors.primary};`">${{ parseFloat(creditBalance).toFixed(2) }}</p>
                                    </div>
                                    <div class="fin-xfer-bal-ic" :style="`background:rgba(${themeColors.primaryRgb},0.1);border-color:rgba(${themeColors.primaryRgb},0.2);color:${themeColors.primary};`">
                                        <svg width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                        </svg>
                                    </div>
                                </div>

                                <form @submit.prevent="submitTransfer" class="fin-xfer-form">
                                    <div class="fin-field">
                                        <label class="fin-field-lbl">Recipient UID</label>
                                        <div class="fin-input-wrap">
                                            <svg class="fin-input-pfx" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                                            </svg>
                                            <input v-model="transferForm.recipient_uid" type="text" required placeholder="Enter member UID" class="fin-input fin-input--icon" />
                                        </div>
                                        <p v-if="transferForm.errors.recipient_uid" class="fin-err">{{ transferForm.errors.recipient_uid }}</p>
                                    </div>
                                    <div class="fin-field">
                                        <label class="fin-field-lbl">Amount (USD)</label>
                                        <div class="fin-input-wrap">
                                            <span class="fin-input-pfx fin-input-pfx--txt">$</span>
                                            <input v-model="transferForm.amount" type="number" step="0.01" min="0.01" :max="creditBalance" required placeholder="0.00" class="fin-input fin-input--icon" />
                                        </div>
                                        <p v-if="transferForm.errors.amount" class="fin-err">{{ transferForm.errors.amount }}</p>
                                    </div>
                                    <p v-if="transferForm.errors.error" class="fin-err fin-err--block">{{ transferForm.errors.error }}</p>
                                    <button type="submit" :disabled="transferForm.processing" class="fin-btn-primary"
                                        :style="`background:linear-gradient(135deg,#7c3aed,${themeColors.primary});box-shadow:0 4px 18px rgba(${themeColors.primaryRgb},0.35);`">
                                        <svg v-if="!transferForm.processing" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                                        </svg>
                                        <svg v-else class="fin-spin" width="14" height="14" fill="none" viewBox="0 0 24 24">
                                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" style="opacity:0.25"/>
                                            <path fill="currentColor" style="opacity:0.75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                        </svg>
                                        {{ transferForm.processing ? 'Processing...' : 'Transfer Credits' }}
                                    </button>
                                </form>
                            </div>

                            <!-- Info sidebar -->
                            <div class="fin-card fin-xfer-sidebar">
                                <div class="fin-card-topline" style="background:linear-gradient(90deg,transparent,rgba(96,165,250,0.4),transparent)"></div>
                                <h3 class="fin-xfer-info-title">How It Works</h3>
                                <div class="fin-xfer-steps">
                                    <div class="fin-xfer-step" v-for="(step, i) in xferSteps" :key="i">
                                        <div class="fin-xfer-step-n" :style="`background:rgba(${themeColors.primaryRgb},0.12);color:${themeColors.primary};border-color:rgba(${themeColors.primaryRgb},0.25);`">{{ i+1 }}</div>
                                        <p class="fin-xfer-step-t">{{ step }}</p>
                                    </div>
                                </div>
                                <div class="fin-xfer-warn">
                                    <svg width="13" height="13" fill="currentColor" viewBox="0 0 20 20" style="flex-shrink:0;color:#fbbf24;margin-top:1px">
                                        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                                    </svg>
                                    <p>Token Credits are non-withdrawable. Transfers cannot be reversed once sent.</p>
                                </div>
                            </div>

                        </div>

                        <!-- ── TRANSFER HISTORY ── -->
                        <div class="fin-card fin-xfer-hist">
                            <div class="fin-card-topline" :style="`background:linear-gradient(90deg,transparent,rgba(${themeColors.primaryRgb},0.4),rgba(232,121,249,0.2),transparent)`"></div>

                            <div class="fin-xfer-hist-head">
                                <div class="fin-xfer-hist-title-row">
                                    <div class="fin-xfer-hist-icon" :style="`background:linear-gradient(135deg,#7c3aed,${themeColors.primary});`">
                                        <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                        </svg>
                                    </div>
                                    <h3 class="fin-xfer-hist-title">Transfer History</h3>
                                </div>
                                <span class="fin-xfer-hist-count" :style="`background:rgba(${themeColors.primaryRgb},0.1);border-color:rgba(${themeColors.primaryRgb},0.25);color:${themeColors.primary};`">
                                    {{ creditTransfers.length }} record{{ creditTransfers.length !== 1 ? 's' : '' }}
                                </span>
                            </div>

                            <!-- Empty -->
                            <div v-if="!creditTransfers.length" class="fin-xfer-hist-empty">
                                <svg width="36" height="36" fill="none" :stroke="`rgba(${themeColors.primaryRgb},0.3)`" stroke-width="1.5" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                                </svg>
                                <p class="fin-xfer-hist-empty-txt">No transfers yet. Send DS-Credits to another member to get started.</p>
                            </div>

                            <!-- Table -->
                            <div v-else class="fin-xfer-hist-table-wrap">
                                <table class="fin-xfer-hist-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Reference</th>
                                            <th>Direction</th>
                                            <th>Counterparty UID</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="tx in creditTransfers" :key="tx.ref + tx.direction">
                                            <td class="fin-xfer-hist-date">{{ tx.date }}</td>
                                            <td class="fin-xfer-hist-ref">{{ tx.ref }}</td>
                                            <td>
                                                <span class="fin-xfer-hist-dir" :class="tx.direction === 'sent' ? 'fin-xfer-hist-dir--out' : 'fin-xfer-hist-dir--in'">
                                                    <svg width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" :d="tx.direction === 'sent' ? 'M5 12h14m0 0l-7-7m7 7l-7 7' : 'M19 12H5m0 0l7-7m-7 7l7 7'"/>
                                                    </svg>
                                                    {{ tx.direction === 'sent' ? 'Sent' : 'Received' }}
                                                </span>
                                            </td>
                                            <td class="fin-xfer-hist-uid">{{ tx.counterparty }}</td>
                                            <td class="fin-xfer-hist-amt" :class="tx.direction === 'sent' ? 'fin-xfer-hist-amt--out' : 'fin-xfer-hist-amt--in'">
                                                {{ tx.direction === 'sent' ? '-' : '+' }}${{ parseFloat(tx.amount).toFixed(2) }}
                                            </td>
                                            <td>
                                                <span class="fin-xfer-hist-status" :class="`fin-xfer-hist-status--${tx.status}`">{{ tx.status }}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                    </div>

                </div><!-- /fin-panels -->

            </div><!-- /fin-inner -->
        </div><!-- /fin-wrap -->
    </UserLayout>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue';
import {usePage} from '@inertiajs/vue3';
import {useForm} from '@inertiajs/vue3';
import UserLayout from "@/Layouts/UserLayout/UserLayout.vue";
import DepositComponent from "@/Components/User/Finance/DepositComponent.vue";
import WithdrawComponent from "@/Components/User/Finance/WithdrawComponent.vue";
import RioConvertComponent from "@/Components/User/Finance/RioConvertComponent.vue";
import {useToast} from "@/composables/useToast.js";
import {useTranslation} from '@/composables/useTranslation';

const props = defineProps({
    deposits: { type: Object, default: () => ({data: [], total: 0, links: []}) },
    withdrawals: { type: Object, default: () => ({data: [], total: 0, links: []}) },
    paymentGateways: { type: Array, default: () => [] },
    withdrawalGateways: { type: Array, default: () => [] },
    walletBalance:   { type: [Number, String], default: 0 },
    creditBalance:   { type: [Number, String], default: 0 },
    rioBalance:      { type: [Number, String], default: 0 },
    ewalletBalance:  { type: [Number, String], default: 0 },
    rioConversions:  { type: Array, default: () => [] },
    creditTransfers: { type: Array, default: () => [] },
    bscWallets:      { type: Array, default: () => [] },
});

const activeTab = ref('deposit');
const {showToast} = useToast();
const {t} = useTranslation();
const page = usePage();

const xferSteps = ['Enter the recipient UID and the amount you want to send.', 'Credits transfer instantly to the recipient\'s account.', 'Recipient can use credits for investments on the platform.'];

const tabs = computed(() => [
    { value: 'deposit',  label: t('depositFunds'),  icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>' },
    { value: 'withdraw', label: t('withdrawFunds'), icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/>' },
    { value: 'rio',      label: 'Convert Rio BTC',  icon: '<circle cx="12" cy="12" r="9"/><path d="M9 7h5a2 2 0 010 4H9m0 0h5.5a2 2 0 010 4H9m0-4v-4m0 8V7"/>' },
    { value: 'transfer', label: 'Transfer Credits', icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>' }
]);

const transferForm = useForm({ recipient_uid: '', amount: '' });

const submitTransfer = () => {
    transferForm.post('/user/wallet/transfer-credits', {
        preserveScroll: true,
        onSuccess: () => { transferForm.reset(); showToast('Credits transferred successfully.', 'success'); },
        onError: (errors) => {
            const msg = errors?.error || errors?.recipient_uid || errors?.amount || 'Transfer failed.';
            showToast(msg, 'error');
        }
    });
};

const primaryColor = computed(() => page.props.primaryColor || '#86efac');
const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16)} : {r: 134, g: 239, b: 172};
};
const rgbToHex = (r, g, b) => "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex);
    return rgbToHex(Math.min(255, Math.max(0, rgb.r + amount)), Math.min(255, Math.max(0, rgb.g + amount)), Math.min(255, Math.max(0, rgb.b + amount)));
};
const themeColors = computed(() => ({
    primary: primaryColor.value,
    primaryDark: adjustColor(primaryColor.value, -20),
    primaryLight: adjustColor(primaryColor.value, 40),
    primaryRgb: `${hexToRgb(primaryColor.value).r}, ${hexToRgb(primaryColor.value).g}, ${hexToRgb(primaryColor.value).b}`
}));

const formatBtcAmount = (amount, precision = 12) => new Intl.NumberFormat('en-US', { minimumFractionDigits: precision, maximumFractionDigits: precision }).format(parseFloat(amount) || 0);

onMounted(() => {
    const flash = page.props.flash;
    if (flash?.success) showToast(flash.success, 'success');
    if (flash?.error) showToast(flash.error, 'error');
    if (flash?.warning) showToast(flash.warning, 'warning');
    if (flash?.info) showToast(flash.info, 'info');
});
</script>

<style scoped>
/* ════════ SHELL ════════ */
.fin-wrap  { position:relative; z-index:10; width:100%; }
.fin-inner { max-width:1280px; margin:0 auto; padding:16px 14px; display:flex; flex-direction:column; gap:14px; }
@media(min-width:640px)  { .fin-inner{ padding:20px 20px; gap:16px; } }
@media(min-width:1024px) { .fin-inner{ padding:20px 24px; gap:18px; } }

/* ════════ ANIMATION ════════ */
.anim-up { animation:finUp 0.45s cubic-bezier(0.22,1,0.36,1) both; animation-delay:var(--d,0ms); }
@keyframes finUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }

/* ════════ BASE CARD ════════ */
.fin-card {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.14);
    border-radius: 16px;
    padding: 20px;
    position: relative;
    overflow: hidden;
    transition: border-color 0.25s;
}
.fin-card:hover { border-color: rgba(167,93,237,0.26); }
.fin-card-topline {
    position: absolute;
    top: 0; left: 10%; right: 10%; height: 1px;
    pointer-events: none;
}

/* ════════ HEADER STRIP ════════ */
.fin-header {
    display:flex; align-items:center; justify-content:space-between;
    gap:12px; flex-wrap:wrap;
    padding:13px 18px; border-radius:13px;
    background:rgba(167,93,237,0.06);
    border:1px solid rgba(167,93,237,0.17);
}
.fin-header-l { display:flex; align-items:center; gap:11px; }
.fin-header-icon {
    width:40px; height:40px; border-radius:11px;
    background:linear-gradient(135deg,#7c3aed,#a75ded);
    display:flex; align-items:center; justify-content:center;
    color:#fff; flex-shrink:0;
    box-shadow:0 0 16px rgba(167,93,237,0.42);
}
.fin-header-sup   { font-size:9px; font-weight:700; color:rgba(167,93,237,0.7); text-transform:uppercase; letter-spacing:0.12em; margin:0 0 2px; }
.fin-header-title { font-size:15px; font-weight:900; color:#fff; letter-spacing:-0.02em; margin:0; }
.fin-bal-chips    { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.fin-bal-chip {
    display:flex; flex-direction:column; gap:3px;
    padding:9px 14px; border-radius:10px;
    background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07);
    min-width:100px;
}
.fin-bal-chip--blue { border-color:rgba(96,165,250,0.2); background:rgba(96,165,250,0.05); }
.fin-chip-lbl { font-size:8px; font-weight:700; color:#4b5563; text-transform:uppercase; letter-spacing:0.12em; }
.fin-chip-val { font-size:15px; font-weight:900; line-height:1; }

/* ════════ STAT CARDS ════════ */
.fin-stat-row {
    display:grid;
    grid-template-columns:repeat(2,1fr);
    gap:10px;
}
@media(min-width:768px) { .fin-stat-row{ grid-template-columns:repeat(3,1fr); gap:8px; } }
@media(min-width:1280px) { .fin-stat-row{ grid-template-columns:repeat(6,1fr); gap:8px; } }

.fin-stat {
    background:rgba(10,4,22,0.97);
    border:1px solid rgba(167,93,237,0.12);
    border-radius:13px; padding:13px 14px;
    position:relative; overflow:hidden;
    transition:transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s;
    cursor:default;
}
.fin-stat:hover { transform:translateY(-3px); box-shadow:0 10px 32px rgba(0,0,0,0.4),0 0 20px rgba(var(--ac),0.12); }
.fin-stat-glow { position:absolute; inset:0; pointer-events:none; }
.fin-stat-top  { display:flex; align-items:center; gap:8px; margin-bottom:10px; position:relative; z-index:1; }
.fin-stat-ic   { width:30px; height:30px; border-radius:9px; display:flex; align-items:center; justify-content:center; flex-shrink:0; transition:transform 0.25s; }
.fin-stat:hover .fin-stat-ic { transform:scale(1.1); }
.fin-stat-lbl  { font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:0.12em; color:#374151; }
.fin-stat-val  { font-size:1.1rem; font-weight:900; letter-spacing:-0.02em; line-height:1.1; position:relative; z-index:1; margin:0 0 4px; }
@media(min-width:1024px) { .fin-stat-val{ font-size:1.25rem; } }
.fin-stat-sub  { font-size:10px; font-weight:700; position:relative; z-index:1; margin-bottom:10px; }
.fin-bar-track { height:2px; background:rgba(255,255,255,0.06); border-radius:999px; overflow:hidden; position:relative; z-index:1; }
.fin-bar       { height:100%; border-radius:999px; }
.fin-bar--shimmer { position:relative; overflow:hidden; }
.fin-bar--shimmer::after {
    content:''; position:absolute; inset:0;
    background:linear-gradient(90deg,transparent,rgba(255,255,255,0.28),transparent);
    animation:finShimmer 2s infinite;
}
@keyframes finShimmer { 0%{transform:translateX(-100%)} 100%{transform:translateX(100%)} }

/* ════════ TAB BAR ════════ */
.fin-tabs-wrap { display:flex; justify-content:center; }
.fin-tabs {
    display:flex; align-items:center; gap:4px;
    background:rgba(10,4,22,0.97);
    border:1px solid rgba(167,93,237,0.15);
    border-radius:13px; padding:5px;
    flex-wrap:wrap; justify-content:center;
}
.fin-tab {
    display:inline-flex; align-items:center; gap:7px;
    padding:9px 18px; border-radius:9px;
    font-size:12.5px; font-weight:700; color:rgba(148,163,184,0.65);
    background:transparent; border:none; cursor:pointer;
    transition:all 0.2s; white-space:nowrap;
}
.fin-tab:hover:not(.fin-tab--on) { color:#fff; background:rgba(167,93,237,0.07); }
.fin-tab--on { color:#fff !important; }
.fin-tab-ic  { flex-shrink:0; }
@media(max-width:480px) { .fin-tab{ padding:8px 13px; font-size:11.5px; } }

/* ════════ PANELS ════════ */
.fin-panels { width:100%; }

/* ════════ TRANSFER PANEL ════════ */
.fin-xfer { width:100%; }
.fin-xfer-grid {
    display:grid;
    gap:16px;
}
@media(min-width:768px) { .fin-xfer-grid{ grid-template-columns:1fr 320px; align-items:start; } }

/* Transfer card */
.fin-xfer-head { display:flex; align-items:center; gap:12px; margin-bottom:22px; }
.fin-xfer-icon {
    width:44px; height:44px; border-radius:12px; border:1px solid;
    display:flex; align-items:center; justify-content:center; flex-shrink:0;
}
.fin-xfer-title { font-size:15px; font-weight:800; color:#fff; margin:0 0 3px; letter-spacing:-0.01em; }
.fin-xfer-sub   { font-size:11.5px; color:rgba(148,163,184,0.5); margin:0; }

.fin-xfer-balance {
    display:flex; align-items:center; justify-content:space-between;
    padding:13px 16px; border-radius:12px; border:1px solid;
    margin-bottom:22px;
}
.fin-xfer-bal-lbl { font-size:10px; font-weight:700; color:rgba(148,163,184,0.5); text-transform:uppercase; letter-spacing:0.07em; margin:0 0 4px; }
.fin-xfer-bal-val { font-size:22px; font-weight:900; line-height:1; margin:0; }
.fin-xfer-bal-ic  { width:38px; height:38px; border-radius:10px; border:1px solid; display:flex; align-items:center; justify-content:center; flex-shrink:0; }

/* Form */
.fin-xfer-form { display:flex; flex-direction:column; gap:16px; }
.fin-field      { display:flex; flex-direction:column; gap:7px; }
.fin-field-lbl  { font-size:11.5px; font-weight:700; color:rgba(226,232,240,0.8); }
.fin-input-wrap { position:relative; display:flex; align-items:center; }
.fin-input-pfx  { position:absolute; left:13px; color:rgba(148,163,184,0.45); pointer-events:none; font-size:13px; }
.fin-input-pfx--txt { font-weight:800; }
.fin-input {
    width:100%; padding:12px 14px;
    background:rgba(255,255,255,0.04);
    border:1px solid rgba(167,93,237,0.18);
    border-radius:11px;
    font-size:13.5px; font-weight:600; color:#fff;
    outline:none; transition:border-color 0.2s, background 0.2s;
}
.fin-input--icon { padding-left:38px; }
.fin-input:focus { border-color:rgba(167,93,237,0.45); background:rgba(167,93,237,0.04); }
.fin-input::placeholder { color:rgba(148,163,184,0.3); }
.fin-err        { font-size:11px; font-weight:600; color:#f87171; margin:0; }
.fin-err--block { padding:9px 12px; border-radius:9px; background:rgba(239,68,68,0.08); border:1px solid rgba(239,68,68,0.22); }

.fin-btn-primary {
    width:100%; padding:13px 20px;
    border-radius:12px; border:none;
    font-size:13.5px; font-weight:800; color:#fff;
    cursor:pointer; display:inline-flex; align-items:center; justify-content:center; gap:8px;
    transition:all 0.2s; letter-spacing:0.01em;
}
.fin-btn-primary:hover:not(:disabled) { transform:translateY(-2px); filter:brightness(1.1); }
.fin-btn-primary:disabled { opacity:0.55; cursor:not-allowed; }
.fin-spin { animation:finSpin 0.85s linear infinite; }
@keyframes finSpin { to{transform:rotate(360deg)} }

/* Sidebar */
.fin-xfer-sidebar { }
.fin-xfer-info-title { font-size:13px; font-weight:800; color:#fff; margin:0 0 16px; }
.fin-xfer-steps  { display:flex; flex-direction:column; gap:13px; margin-bottom:18px; }
.fin-xfer-step   { display:flex; align-items:flex-start; gap:10px; }
.fin-xfer-step-n {
    width:26px; height:26px; border-radius:8px; border:1px solid;
    display:flex; align-items:center; justify-content:center;
    font-size:11px; font-weight:900; flex-shrink:0;
}
.fin-xfer-step-t { font-size:12px; font-weight:600; color:rgba(148,163,184,0.65); line-height:1.55; padding-top:4px; }
.fin-xfer-warn {
    display:flex; align-items:flex-start; gap:9px;
    padding:11px 13px; border-radius:10px;
    background:rgba(251,191,36,0.07); border:1px solid rgba(251,191,36,0.2);
    font-size:11px; font-weight:600; color:rgba(253,230,138,0.7); line-height:1.5;
}

/* Rio Wallet: compact front */
.fin-stat--rio { padding:10px 10px; border-radius:11px; }
.fin-stat--rio .fin-stat-ic { width:26px; height:26px; border-radius:8px; }
.fin-stat--rio .fin-stat-lbl { font-size:8px; }
.fin-stat--rio .fin-stat-val { font-size:0.95rem; margin-bottom:2px; }
.fin-stat--rio .fin-stat-sub { font-size:9px; margin-bottom:6px; }

/* ── Transfer History ── */
.fin-xfer-hist { margin-top: 18px; }
.fin-xfer-hist-head {
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; margin-bottom: 20px; flex-wrap: wrap;
}
.fin-xfer-hist-title-row { display: flex; align-items: center; gap: 10px; }
.fin-xfer-hist-icon {
    width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    color: #0f172a; box-shadow: 0 3px 12px rgba(124,58,237,0.35);
}
.fin-xfer-hist-title { font-size: 14px; font-weight: 800; color: #fff; margin: 0; }
.fin-xfer-hist-count {
    font-size: 11px; font-weight: 700; padding: 3px 10px; border-radius: 20px; border: 1px solid;
}
.fin-xfer-hist-empty {
    display: flex; flex-direction: column; align-items: center; gap: 14px;
    padding: 40px 20px; text-align: center;
}
.fin-xfer-hist-empty-txt { font-size: 13px; color: rgba(148,163,184,0.45); font-weight: 600; }
.fin-xfer-hist-table-wrap { overflow-x: auto; border-radius: 12px; border: 1px solid rgba(255,255,255,0.06); }
.fin-xfer-hist-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
.fin-xfer-hist-table thead tr {
    background: rgba(124,58,237,0.06); border-bottom: 1px solid rgba(124,58,237,0.18);
}
.fin-xfer-hist-table th {
    padding: 11px 14px; text-align: left; font-size: 11px; font-weight: 700;
    color: rgba(167,93,237,0.7); text-transform: uppercase; letter-spacing: 0.07em; white-space: nowrap;
}
.fin-xfer-hist-table tbody tr {
    border-bottom: 1px solid rgba(255,255,255,0.04); transition: background 0.15s;
}
.fin-xfer-hist-table tbody tr:last-child { border-bottom: none; }
.fin-xfer-hist-table tbody tr:hover { background: rgba(124,58,237,0.04); }
.fin-xfer-hist-table td { padding: 12px 14px; vertical-align: middle; white-space: nowrap; }
.fin-xfer-hist-date { font-size: 11.5px; color: rgba(148,163,184,0.55); }
.fin-xfer-hist-ref  { font-size: 11px; font-family: monospace; color: rgba(148,163,184,0.5); }
.fin-xfer-hist-uid  { font-size: 12px; font-weight: 700; color: rgba(226,232,240,0.8); }
.fin-xfer-hist-amt  { font-weight: 800; font-size: 13px; }
.fin-xfer-hist-amt--out { color: #f87171; }
.fin-xfer-hist-amt--in  { color: #10b981; }
.fin-xfer-hist-dir {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 9px; border-radius: 6px; font-size: 11px; font-weight: 700;
}
.fin-xfer-hist-dir--out { background: rgba(239,68,68,0.1); color: #f87171; border: 1px solid rgba(239,68,68,0.2); }
.fin-xfer-hist-dir--in  { background: rgba(16,185,129,0.1); color: #10b981; border: 1px solid rgba(16,185,129,0.2); }
.fin-xfer-hist-status {
    display: inline-block; padding: 3px 9px; border-radius: 6px;
    font-size: 11px; font-weight: 700; text-transform: capitalize;
}
.fin-xfer-hist-status--completed { background: rgba(16,185,129,0.12); color: #10b981; border: 1px solid rgba(16,185,129,0.2); }
.fin-xfer-hist-status--pending   { background: rgba(251,191,36,0.12);  color: #fbbf24; border: 1px solid rgba(251,191,36,0.2); }
.fin-xfer-hist-status--failed    { background: rgba(239,68,68,0.12);   color: #f87171; border: 1px solid rgba(239,68,68,0.2); }

</style>
